'use client';

// The sidebar's sections and their nav entries. Split out of Sidebar.tsx
// (BL-2422), which also added the offline state: in the phone app, while
// offline, an entry outside the offline-capable areas stays visible but is
// disabled with the no-wifi mark (ADR 066 — Offline Mode).

import { useId } from 'react';
import { Lock, WifiOff } from 'lucide-react';
import { controlNoAccessLine } from '@auctogon/core-admin/api/no-access-copy';
import { clsx } from 'clsx';
import { isOfflineBlocked } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';
import { useOfflineMode } from '../offline/OfflineModeProvider';
import { useChangeQueueIssueCount } from '../offline/ChangeQueueCountBridge';
import { useInboxUnreadCount, type InboxUnreadCount } from '../inbox/InboxCountBridge';
import { NEEDS_CONNECTION } from '../offline/offline-copy';
import NeedsConnectionMark from '../offline/NeedsConnectionMark';
import Badge, { ALERT_COUNT_CLASSES } from '../ui/Badge';
import type { NavItem, NavSection } from './nav-config';

/** Props for {@link SidebarNavSection}. */
export interface SidebarNavSectionProps {
  section: NavSection;
  isCollapsed: boolean;
  pathname: string;
  searchParams: URLSearchParams;
  canSeeNav: (href: string) => boolean;
  /** Whether the page runs inside the native admin app (BL-2342). */
  isNativeApp: boolean;
  onNavigate: (href: string) => void;
}

/**
 * One titled group of entries. Every role sees the same entries in the same places:
 * one the role can't open renders locked in place (BL-2519, BUG-2187). The only
 * entry dropped by role is Super Admin, ADR 030 — Design Canon's closed exception.
 *
 * @param props - see {@link SidebarNavSectionProps}
 * @returns The section, or null when nothing in it is visible.
 */
export function SidebarNavSection({
  section,
  isCollapsed,
  pathname,
  searchParams,
  canSeeNav,
  isNativeApp,
  onNavigate,
}: SidebarNavSectionProps) {
  if (section.nativeAppOnly && !isNativeApp) return null;

  const visibleItems = section.items.filter((item) => !isSuperAdminEntry(item.href) || canSeeNav(item.href));
  if (visibleItems.length === 0) return null;

  return (
    <div>
      {!isCollapsed && (
        <h3 className="px-3 text-caption font-semibold text-sidebar-text/60 uppercase tracking-wider mb-2">
          {section.title}
        </h3>
      )}
      <div className="space-y-density-tight">
        {visibleItems.map((item) => (
          <SidebarNavItem
            key={item.name}
            item={item}
            isCollapsed={isCollapsed}
            pathname={pathname}
            searchParams={searchParams}
            canSeeNav={canSeeNav}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

/** Whether the entry is the Super Admin console, the one entry a role hides (ADR 030 — Design Canon). */
function isSuperAdminEntry(href: string): boolean {
  return href === '/super-admin';
}

/** Props for {@link SidebarNavItem}. */
export interface SidebarNavItemProps {
  item: NavItem;
  isCollapsed: boolean;
  pathname: string;
  searchParams: URLSearchParams;
  /** The section's own gate, so the entry's lock and the section's filter read one source (BL-2519). */
  canSeeNav: (href: string) => boolean;
  onNavigate: (href: string) => void;
}

/**
 * The automation handle for an entry, derived from `href`, not `item.name`:
 * the label is copy that gets reworded, while the route is what the entry IS.
 * The query string is part of the identity, so two entries sharing a pathname
 * and differing by `?tab=` keep distinct handles.
 *
 * @param href - the entry's route.
 * @returns e.g. `nav-customers`.
 */
function navTestIdFor(href: string): string {
  return `nav-${href.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'home'}`;
}

/** An entry's count, as the entry shows it. */
interface NavCount {
  value: number;
  /** The count in words, for the accessible name: "2 changes need attention", "3 unread". */
  text: string;
  /** `alert` is the solid red count of changes that need someone (BL-2569); `brand` the Inbox's chip. */
  tone: 'brand' | 'alert';
}

/**
 * The count an entry carries, or null when it carries none: no count variant, nothing to count,
 * or an Inbox count that could not be loaded (no chip rather than a false number).
 *
 * @param variant - the entry's count variant.
 * @param issues - the change queue's count of changes that need someone (BL-2569).
 * @param inbox - the Inbox's unread count, or null with no bridge mounted.
 * @returns the count to show.
 */
function navCountFor(
  variant: NavItem['count'],
  issues: number | null,
  inbox: InboxUnreadCount | null,
): NavCount | null {
  if (variant === 'change-queue-issues' && issues != null && issues > 0) {
    return { value: issues, text: `${issues} ${issues === 1 ? 'change needs' : 'changes need'} attention`, tone: 'alert' };
  }
  if (variant === 'inbox-unread' && inbox != null && !inbox.unavailable && inbox.count > 0) {
    return { value: inbox.count, text: `${inbox.count} unread`, tone: 'brand' };
  }
  return null;
}

/**
 * One nav entry. Reads the offline mode and its count itself (ADR 029 — View
 * Units): no caller says whether it is disabled or what it counts.
 *
 * @param props - see {@link SidebarNavItemProps}
 * @returns The entry's button.
 */
export function SidebarNavItem({ item, isCollapsed, pathname, searchParams, canSeeNav, onNavigate }: SidebarNavItemProps) {
  const { isOffline } = useOfflineMode();
  const queueIssues = useChangeQueueIssueCount();
  const inboxCount = useInboxUnreadCount();
  const navTestId = navTestIdFor(item.href);
  const count = navCountFor(item.count, queueIssues, inboxCount);
  const reasonId = useId();

  if (isOfflineBlocked(item.href, isOffline)) {
    return <OfflineDisabledNavItem item={item} isCollapsed={isCollapsed} testId={navTestId} />;
  }

  const isActive = computeIsActive(item, pathname, searchParams);
  // Locked, not hidden (BL-2519, BUG-2187): the reason rides the title (hover) and the accessible
  // description (screen readers), and the entry still opens its page, whose No access panel
  // carries the reason on touch.
  const lockReason = item.lockedGrant != null && !canSeeNav(item.href) ? controlNoAccessLine({ grant: item.lockedGrant }) : null;
  const countedName = count == null ? item.name : `${item.name}, ${count.text}`;
  const lockedName = lockReason == null ? countedName : `${countedName}, locked`;
  // A bare chip needs the name to say what it counts; collapsed, only the icon shows, so the name
  // and the title carry the label.
  const label = isCollapsed || count != null || lockReason != null ? lockedName : undefined;
  return (
    <>
      {/* eslint-disable-next-line designsystem/no-raw-button -- non-CTA navigation row on the dark nav rail */}
      <button
        type="button"
        // Collapsed, the tooltip is the only visible name, so a locked entry leads with it.
        title={lockReason == null ? (isCollapsed ? label : undefined) : isCollapsed ? `${item.name}: ${lockReason}` : lockReason}
        aria-label={label}
        aria-describedby={lockReason == null ? undefined : reasonId}
        data-state={lockReason == null ? undefined : 'locked'}
        className={clsx(
          isActive
            ? 'bg-sidebar-border text-secondary'
            : 'text-sidebar-text/80 hover:bg-sidebar-border hover:text-sidebar-text',
          'group relative flex w-full items-center text-body font-medium rounded-md transition-colors',
          isCollapsed ? 'justify-center py-2' : 'px-3 py-2 text-left'
        )}
        data-testid={navTestId}
        onClick={() => onNavigate(item.href)}
      >
        <span className={clsx('relative flex-shrink-0', !isCollapsed && 'mr-3')}>
          <item.icon
            className={clsx(
              isActive ? 'text-secondary' : 'text-sidebar-text/70 group-hover:text-sidebar-text',
              'h-5 w-5 transition-colors'
            )}
            aria-hidden
          />
          {/* Collapsed, the padlock sits on the icon's corner, as the offline mark does. */}
          {lockReason != null && isCollapsed && <Lock className="absolute -bottom-1 -right-1 h-3 w-3" aria-hidden />}
        </span>
        {!isCollapsed && <span className="flex-1 text-left">{item.name}</span>}
        {lockReason != null && !isCollapsed && (
          <Lock className="ml-2 h-4 w-4 flex-shrink-0" aria-hidden data-testid={`${navTestId}-lock`} />
        )}
        {count != null && (
          <NavCountBadge count={count.value} tone={count.tone} isCollapsed={isCollapsed} testId={`${navTestId}-badge`} />
        )}
      </button>
      {/* `hidden` keeps it out of view and out of the section's `space-y` spacing; aria-describedby still reads it. */}
      {lockReason != null && (
        <span id={reasonId} hidden>
          {lockReason}
        </span>
      )}
    </>
  );
}

/**
 * An entry that needs a connection the app doesn't have (BL-2422, mockup
 * screen 6). The Tabs BL-1548 shape: `aria-disabled`, NOT native `disabled`,
 * so it stays in the tab order and a keyboard or screen-reader user still
 * reaches the reason; it has no click handler, so nothing navigates.
 *
 * The muted label is `--sidebar-text` at 60% on `--sidebar-bg`:
 *   #829f9d on #09514d ....... 3.22:1  text (needs 4.5) — exempt, disabled
 * WCAG 2.2 1.4.3 exempts a disabled control, as `Tabs` does for its 2.54:1
 * disabled tab. The second signal (ADR 051 — Contrast Floor rule 3) is the
 * no-wifi mark plus `aria-disabled`.
 */
function OfflineDisabledNavItem({
  item,
  isCollapsed,
  testId,
}: {
  item: NavItem;
  isCollapsed: boolean;
  testId: string;
}) {
  // Collapsed, there is no room for the label's trailing mark, so the name
  // carries the reason and a small mark sits on the icon instead.
  const collapsedLabel = isCollapsed ? `${item.name} — ${NEEDS_CONNECTION.toLowerCase()}` : undefined;
  return (
    // eslint-disable-next-line designsystem/no-raw-button -- non-CTA navigation row on the dark nav rail (offline, needs connection)
    <button
      type="button"
      aria-disabled="true"
      data-state="needs-connection"
      data-testid={testId}
      title={collapsedLabel ?? NEEDS_CONNECTION}
      aria-label={collapsedLabel}
      className={clsx(
        'flex w-full cursor-default items-center rounded-md text-body font-medium text-sidebar-text/60',
        isCollapsed ? 'justify-center py-2' : 'px-3 py-2 text-left'
      )}
    >
      {isCollapsed ? (
        // Collapsed, the mark sits on the icon's corner: the fade alone would
        // be the only visible signal, and a touch rail never shows the title.
        <span className="relative flex-shrink-0">
          <item.icon className="h-5 w-5" aria-hidden />
          <WifiOff className="absolute -bottom-1 -right-1 h-3 w-3" aria-hidden />
        </span>
      ) : (
        <item.icon className="mr-3 h-5 w-5 flex-shrink-0" aria-hidden />
      )}
      {!isCollapsed && (
        <>
          <span className="flex-1 text-left">{item.name}</span>
          <NeedsConnectionMark />
        </>
      )}
    </button>
  );
}

/**
 * The count chip on a nav entry (BL-2491, BL-2569): the canonical count `Badge`, beside the label,
 * or on the icon's corner when the rail is collapsed. The entry's accessible name carries the
 * meaning; the chip is the at-a-glance number. `brand` (teal-800 on teal-100, measured in Badge's
 * TONE_CLASSES) is the Inbox's light chip; `alert` is the solid red circle with a white number
 * (`ALERT_COUNT_CLASSES`, white on red-600 4.83:1) for changes that need someone.
 */
function NavCountBadge({
  count,
  tone,
  isCollapsed,
  testId,
}: {
  count: number;
  tone: NavCount['tone'];
  isCollapsed: boolean;
  testId: string;
}) {
  return (
    <span
      aria-hidden
      data-testid={testId}
      data-count-kind={tone}
      className={clsx(isCollapsed ? 'absolute -right-1 -top-1' : 'ml-2 flex-shrink-0')}
    >
      {tone === 'alert' ? (
        <Badge count tone="custom" className={ALERT_COUNT_CLASSES}>
          {count}
        </Badge>
      ) : (
        <Badge count tone="brand">
          {count}
        </Badge>
      )}
    </span>
  );
}

function computeIsActive(item: NavItem, pathname: string, searchParams: URLSearchParams): boolean {
  if (item.href.includes('?tab=')) {
    const [basePath, queryString] = item.href.split('?');
    const params = new URLSearchParams(queryString);
    const tabParam = params.get('tab');
    const currentTab = searchParams.get('tab');
    return pathname === basePath && currentTab === tabParam;
  }
  return pathname === item.href || (item.href !== '/' && pathname.startsWith(`${item.href}/`));
}
